import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useMeta } from "../../../context/MetaContext";
import WordPressToolLandingPage from "../../../pages/WordPressToolLandingPage";

export default function ToolPublicLandingPage() {
  const { toolSlug } = useParams();
  const { isAuthenticated } = useAuth();
  const { getToolBySlug, metaLoading } = useMeta();
  const tool = getToolBySlug(toolSlug);

  if (metaLoading) {
    return (
      <main className="page-grid">
        <section className="card-modern">
          <p>Loading tool configuration...</p>
        </section>
      </main>
    );
  }

  if (!tool) {
    return <Navigate to="/" replace />;
  }

  if (tool.slug === "wpa") {
    return <WordPressToolLandingPage />;
  }

  const entryPath = isAuthenticated ? tool.workspacePath : `/login?next=${encodeURIComponent(tool.workspacePath)}`;
  const highlightCards = [
    {
      icon: "AF",
      title: "Assessment Framework",
      text: tool.featureBlocks?.[0]?.detail || "Structured assessments with evidence-backed scoring."
    },
    {
      icon: "OI",
      title: "Operational Intelligence",
      text: tool.featureBlocks?.[1]?.detail || "Capture incidents and blockers in one execution flow."
    },
    {
      icon: "DS",
      title: "Decision Support",
      text: tool.featureBlocks?.[2]?.detail || "Create recommendation narratives for stakeholder alignment."
    },
    {
      icon: "RO",
      title: "Results Orientation",
      text: tool.outcomeHighlights?.[0] || "Faster diagnosis and stronger execution confidence."
    }
  ];

  return (
    <main className="page-grid wpa-landing">
      <section className="wpa-hero">
        <div className="wpa-hero-copy">
          <p className="landing-eyebrow">{tool.industry} | Tool #{tool.rank}</p>
          <h2>{tool.name}</h2>
          <p>{tool.summary}</p>
          <div className="wpa-hero-chips">
            {tool.heroPoints.map((point) => <span key={point}>{point}</span>)}
          </div>
          <p className="muted">Public permalink: <strong>{tool.permalink}</strong></p>
        </div>

        <div className="wpa-entry-spotlight">
          <p className="landing-eyebrow">Ready To Begin</p>
          <h3>Enter the live workspace and continue from your latest assessment context</h3>
          <p>Sign in once and you will be redirected directly into the {tool.name} workspace.</p>
          <div className="wpa-entry-actions">
            <Link to={entryPath} className="wpa-entry-button">
              Enter {tool.name} Workspace
            </Link>
          </div>
        </div>
      </section>

      <section className="wpa-highlights">
        <p className="landing-eyebrow">Assessment Highlights</p>
        <h3>Everything needed for effective execution and measurable outcomes</h3>
        <div className="wpa-highlights-grid">
          {highlightCards.map((item) => (
            <article key={item.title} className="wpa-highlight-card">
              <span className="wpa-highlight-icon">{item.icon}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="wpa-feature-grid">
        <article className="card-modern">
          <h4>What This Tool Covers</h4>
          <ul>
            {tool.heroPoints.map((point) => <li key={point}>{point}</li>)}
          </ul>
        </article>
        <article className="card-modern">
          <h4>Expected Outcomes</h4>
          <ul>
            {tool.outcomeHighlights.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </article>
        <article className="card-modern">
          <h4>Workflow</h4>
          <ol>
            {tool.workflow.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </article>
        <article className="card-modern">
          <h4>Workspace Access</h4>
          <p className="muted">Workspace path: <strong>{tool.workspacePath}</strong></p>
          <p>Landing is public. Workspace execution is protected and opens immediately after login.</p>
        </article>
      </section>

      <section className="card-modern wpa-bottom-note">
        <h4>Designed For Repeatable, High-Trust Delivery</h4>
        <p>
          Use this tool framework to standardize discovery, evidence capture, and recommendation quality while
          reducing execution friction for teams and stakeholders.
        </p>
        <div className="hero-actions">
          <Link to={entryPath} className="button-link">Open Workspace</Link>
        </div>
      </section>

      <section className="card-modern landing-flow">
        <h3>How This Tool Works</h3>
        <div className="flow-grid">
          {tool.workflow.map((step, index) => (
            <article key={step}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h5>Step {index + 1}</h5>
              <p>{step}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
